/**
 * Repository — data access layer.
 * Currently reads from local JSON. Swap this file's internals to fetch from
 * Supabase / Postgres later without touching UI components.
 *
 * Product record fields (see products.json):
 *   id, sku, slug, name, description, category (slug of one of 20),
 *   department (men/women/kids/accessories), collections (array),
 *   season, price, salePrice, discount, currency, materials, care,
 *   sizes, colors, variants, images, rating, reviewCount, reviews,
 *   tags, featured, newIn, seo
 */
import productsData from '../data/products.json';
import categoriesData from '../data/categories.json';
import journalData from '../data/journal.json';
import lookbookData from '../data/lookbook.json';
import testimonialsData from '../data/testimonials.json';

const ALL = productsData;

// Departments derived from categories
export const DEPARTMENTS = Array.from(
  new Map(categoriesData.map((c) => [c.department, c.department])).keys()
);

// Curated collection metadata (routed via /shop/:slug)
export const COLLECTIONS = {
  'new-arrivals': { name: 'New Arrivals', tagline: 'The pieces just in.' },
  'sale':         { name: 'Sale',          tagline: 'Considered pieces, considered prices.' },
  'summer':       { name: 'Summer',        tagline: 'Linen, poplin, silk. For the long light.' },
  'winter':       { name: 'Winter',        tagline: 'Wool, cashmere, weight. For the long quiet.' },
  'essentials':   { name: 'Essentials',    tagline: 'The wardrobe we return to.' },
  'featured':     { name: 'Featured',      tagline: 'This season, closely watched.' },
};

const delay = (ms = 0) => new Promise((r) => setTimeout(r, ms));

/**
 * Resolve a slug (from URL) into a filter spec.
 * A slug may be: a department, a category, or a collection.
 */
export function resolveSlug(slug) {
  if (!slug) return { kind: 'all' };
  if (COLLECTIONS[slug]) return { kind: 'collection', slug };
  if (DEPARTMENTS.includes(slug)) return { kind: 'department', slug };
  if (categoriesData.some((c) => c.slug === slug)) return { kind: 'category', slug };
  return { kind: 'unknown', slug };
}

function priceOf(p) { return p.salePrice || p.price; }

function applyFilters(items, { sizes, colors, minPrice, maxPrice }) {
  let out = items;
  if (sizes?.length)  out = out.filter((p) => p.sizes.some((s) => sizes.includes(s)));
  if (colors?.length) out = out.filter((p) => p.colors.some((c) => colors.includes(c.name)));
  if (minPrice != null) out = out.filter((p) => priceOf(p) >= minPrice);
  if (maxPrice != null) out = out.filter((p) => priceOf(p) <= maxPrice);
  return out;
}

function applySort(items, sort) {
  const out = [...items];
  if (sort === 'price-asc')  out.sort((a, b) => priceOf(a) - priceOf(b));
  if (sort === 'price-desc') out.sort((a, b) => priceOf(b) - priceOf(a));
  if (sort === 'newest')     out.sort((a, b) => (b.newIn ? 1 : 0) - (a.newIn ? 1 : 0));
  if (sort === 'rating')     out.sort((a, b) => b.rating - a.rating);
  return out;
}

export const repository = {
  async listProducts({ department, category, collection, sort, sizes, colors, minPrice, maxPrice } = {}) {
    await delay();
    let items = [...ALL];
    if (department) items = items.filter((p) => p.department === department);
    if (category)   items = items.filter((p) => p.category === category);
    if (collection) items = items.filter((p) => p.collections.includes(collection));
    items = applyFilters(items, { sizes, colors, minPrice, maxPrice });
    return applySort(items, sort);
  },

  async getProduct(slug) {
    await delay();
    return ALL.find((p) => p.slug === slug) || null;
  },

  async listCategories() {
    await delay();
    return categoriesData;
  },

  categoriesByDepartment() {
    const map = {};
    categoriesData.forEach((c) => {
      (map[c.department] = map[c.department] || []).push(c);
    });
    return map;
  },

  async listFeatured(limit = 8) {
    await delay();
    return ALL.filter((p) => p.featured).slice(0, limit);
  },

  async listRelated(product, limit = 4) {
    await delay();
    return ALL.filter((p) => p.category === product.category && p.slug !== product.slug).slice(0, limit);
  },

  async listJournal() { await delay(); return journalData; },
  async getArticle(slug) { await delay(); return journalData.find((a) => a.slug === slug) || null; },
  async getLookbook() { await delay(); return lookbookData; },
  async listTestimonials() { await delay(); return testimonialsData; },

  /**
   * Instant search across name, description, category, tags, department.
   * Returns { products, categories, suggestions }.
   *
   * For categories: match on category name/slug first; then fall back to
   * deriving matching categories from products whose tags/materials matched
   * the query (so "Merino" surfaces the knitwear categories).
   */
  search(query, limit = 8) {
    const q = (query || '').trim().toLowerCase();
    if (!q) return { products: [], categories: [], suggestions: [] };
    const products = ALL.filter((p) => {
      return (
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.category.includes(q) ||
        p.department.includes(q) ||
        p.tags.some((t) => t.includes(q)) ||
        (p.materials || []).some((m) => m.toLowerCase().includes(q)) ||
        (p.colors || []).some((c) => c.name.toLowerCase().includes(q))
      );
    }).slice(0, limit);

    // Categories: direct name/slug hits + derived from matching product categories
    const directCats = categoriesData.filter((c) => c.name.toLowerCase().includes(q) || c.slug.includes(q));
    const derivedCatSlugs = Array.from(new Set(products.map((p) => p.category)));
    const derivedCats = categoriesData.filter((c) => derivedCatSlugs.includes(c.slug) && !directCats.some((d) => d.slug === c.slug));
    const categories = [...directCats, ...derivedCats].slice(0, 4);

    // Autocomplete suggestions from product names (first-word matches)
    const suggestions = Array.from(new Set(
      ALL
        .filter((p) => p.name.toLowerCase().startsWith(q) || p.name.toLowerCase().includes(' ' + q))
        .map((p) => p.name)
    )).slice(0, 5);

    return { products, categories, suggestions };
  },
};

export const POPULAR_SEARCHES = [
  'Overcoat', 'Merino', 'Linen', 'Denim', 'Cashmere', 'Leather', 'Sneakers', 'Kids', 'Silk', 'Wool',
];
